import apiClient from './api';
import {
  ScheduledInstance,
  ShootData,
  ScheduledInstanceFilters,
  RescheduleData,
  ScheduledInstanceResponse,
  ScheduledInstancesListResponse,
} from '@/types/scheduled-instances';

/**
 * Scheduled Instances Service
 *
 * API calls for the Shoot phase (committing calendar mappings to publishing)
 */

/**
 * Helper to serialize dates to ISO strings
 */
const serializeDate = (date: Date | string): string => {
  if (typeof date === 'string') return date;
  return date.toISOString();
};

/**
 * Helper to build query string from filters
 */
const buildQueryString = (filters: ScheduledInstanceFilters): string => {
  const params = new URLSearchParams();

  if (filters.calendarMappingId !== undefined) {
    params.append('calendarMappingId', filters.calendarMappingId.toString());
  }
  if (filters.status) {
    params.append('status', filters.status);
  }
  if (filters.scheduledFrom) {
    params.append('scheduledFrom', serializeDate(filters.scheduledFrom));
  }
  if (filters.scheduledTo) {
    params.append('scheduledTo', serializeDate(filters.scheduledTo));
  }

  const queryString = params.toString();
  return queryString ? `?${queryString}` : '';
};

export const scheduledInstancesService = {
  /**
   * Shoot - Commit a calendar mapping to publishing
   * Creates a scheduled instance that will publish at the specified time
   */
  shoot: async (data: ShootData): Promise<ScheduledInstance> => {
    const payload = {
      ...data,
      scheduledAt: serializeDate(data.scheduledAt),
    };

    const response = await apiClient.post<ScheduledInstanceResponse>(
      '/api/scheduled-instances/shoot',
      payload
    );
    return response.data.data;
  },

  /**
   * List scheduled instances with optional filters
   */
  listInstances: async (filters?: ScheduledInstanceFilters): Promise<ScheduledInstance[]> => {
    const queryString = filters ? buildQueryString(filters) : '';
    const response = await apiClient.get<ScheduledInstancesListResponse>(
      `/api/scheduled-instances${queryString}`
    );
    return response.data.data;
  },

  /**
   * Get a single scheduled instance with all details
   */
  getInstance: async (id: number): Promise<ScheduledInstance> => {
    const response = await apiClient.get<ScheduledInstanceResponse>(
      `/api/scheduled-instances/${id}`
    );
    return response.data.data;
  },

  /**
   * Cancel a scheduled instance (before publishing starts)
   * Only works for instances in QUEUED status
   */
  cancelInstance: async (id: number): Promise<ScheduledInstance> => {
    const response = await apiClient.post<ScheduledInstanceResponse>(
      `/api/scheduled-instances/${id}/cancel`
    );
    return response.data.data;
  },

  /**
   * Reschedule an instance to a new time
   * Only works for instances in QUEUED status
   */
  rescheduleInstance: async (
    id: number,
    data: RescheduleData
  ): Promise<ScheduledInstance> => {
    const payload = {
      newScheduledAt: serializeDate(data.newScheduledAt),
    };

    const response = await apiClient.patch<ScheduledInstanceResponse>(
      `/api/scheduled-instances/${id}/reschedule`,
      payload
    );
    return response.data.data;
  },
};
